import {
  formatCitationRangeHoursLabel,
  isTimeRangePropertyTypeRule,
  normalizeRangeHoursFromApi,
} from "@/src/modules/properties/lib/citation-range-hours";
import {
  formatPropertyRuleSelectValue,
  isPermanentParkingYesNoRuleKey,
  resolvePropertyRuleSelectOptions,
} from "@/src/modules/properties/lib/property-rule-display";
import type {Property, PropertyRule} from "@/src/modules/properties/types";
import {FEATURE_NAMES} from "@/src/modules/routing/utils/constants";

/** Section key → rule `feature` values that may appear on `/me` rules. */
const SECTION_TO_RULE_FEATURES: Record<string, string[]> = {
  permanentparking: ["permanentparking", "permanent_parking"],
};

const RULE_FEATURE_TO_SECTION: Record<string, string> = Object.fromEntries(
  Object.entries(SECTION_TO_RULE_FEATURES).flatMap(([section, features]) =>
    features.map((feature) => [feature, section]),
  ),
);

export type DispatchPropertyRuleRow = {
  key: string;
  label: string;
  value: string;
};

export type DispatchPropertyRuleGroup = {
  featureKey: string;
  featureLabel: string;
  rules: DispatchPropertyRuleRow[];
};

type MePropertyRule = PropertyRule & {
  feature?: string;
  name?: string;
  type?: string;
  options?: string[];
};

function normalizeFeatureKey(feature: string): string {
  return RULE_FEATURE_TO_SECTION[feature] ?? feature;
}

function getGrantedSectionKeys(property: Property): Set<string> {
  return new Set(
    property.sections.filter((section) => section.granted === 1).map((section) => section.key),
  );
}

function ruleMatchesGrantedSection(rule: MePropertyRule, granted: Set<string>): boolean {
  const feature = rule.feature?.trim();
  if (!feature) return false;

  const sectionKey = normalizeFeatureKey(feature);
  if (granted.has(sectionKey)) return true;

  const aliases = SECTION_TO_RULE_FEATURES[sectionKey];
  if (aliases?.includes(feature) && granted.has(sectionKey)) return true;

  return granted.has(feature);
}

function featureLabel(featureKey: string): string {
  const normalized = featureKey.toLowerCase() as keyof typeof FEATURE_NAMES;
  return FEATURE_NAMES[normalized] ?? featureKey;
}

function formatBooleanRuleValue(value: unknown): string {
  if (value === true || value === 1 || value === "1") return "Yes";
  if (value === false || value === 0 || value === "0") return "No";
  return String(value ?? "—");
}

function formatRuleDisplayValue(rule: MePropertyRule): string {
  const {value, type} = rule;
  const featureKey = rule.feature ? normalizeFeatureKey(rule.feature.trim()) : "";
  const selectOptions = resolvePropertyRuleSelectOptions(rule.key, rule.options, {
    name: rule.name,
    feature: rule.feature,
  });

  if (value === undefined || value === null || value === "") return "—";

  if (type === "boolean") return formatBooleanRuleValue(value);

  if (
    type === "number" &&
    featureKey === "permanentparking" &&
    isPermanentParkingYesNoRuleKey(rule.key)
  ) {
    return formatBooleanRuleValue(value);
  }

  if (type === "select" || selectOptions?.length) {
    return formatPropertyRuleSelectValue(value, selectOptions);
  }

  if (type === "number") {
    const n = Number(value);
    return Number.isFinite(n) ? String(n) : String(value);
  }

  if (
    type === "time_range" ||
    isTimeRangePropertyTypeRule({
      type: (type ?? "") as "number" | "boolean" | "select" | "time_range",
      name: rule.name ?? "",
      key: rule.key,
      value,
    })
  ) {
    return formatCitationRangeHoursLabel(normalizeRangeHoursFromApi(value));
  }

  if (typeof value === "boolean") return formatBooleanRuleValue(value);
  if (typeof value === "number") return String(value);
  if (typeof value === "object" && value !== null && "start" in value && "end" in value) {
    return formatCitationRangeHoursLabel(normalizeRangeHoursFromApi(value));
  }

  return String(value);
}

function ruleLabel(rule: MePropertyRule): string {
  if (rule.name?.trim()) return rule.name.trim();
  return rule.key.replace(/_/g, " ");
}

/**
 * Builds dispatcher-readable rule groups from `/me` property payload only
 * (`property.sections` + `property.rules` with feature meta).
 */
export function buildDispatchPropertyRuleGroups(property: Property): DispatchPropertyRuleGroup[] {
  const granted = getGrantedSectionKeys(property);
  if (granted.size === 0) return [];

  const byFeature = new Map<string, DispatchPropertyRuleRow[]>();

  for (const raw of property.rules ?? []) {
    const rule = raw as MePropertyRule;
    if (!ruleMatchesGrantedSection(rule, granted)) continue;

    const featureKey = normalizeFeatureKey(rule.feature!.trim());
    const row: DispatchPropertyRuleRow = {
      key: rule.key,
      label: ruleLabel(rule),
      value: formatRuleDisplayValue(rule),
    };

    const existing = byFeature.get(featureKey) ?? [];
    existing.push(row);
    byFeature.set(featureKey, existing);
  }

  return [...byFeature.entries()]
    .map(([featureKey, rules]) => ({
      featureKey,
      featureLabel: featureLabel(featureKey),
      rules: rules.toSorted((a, b) => a.label.localeCompare(b.label)),
    }))
    .toSorted((a, b) => a.featureLabel.localeCompare(b.featureLabel));
}
